import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from "react-hook-form";
import { useQueryClient } from "react-query";

import { ProgressSpinner } from 'primereact/progressspinner';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Button } from '../../../../../components/ui/Button';

import { AdsDurations, LayoutAdsSwitch, ScreenAdsSwitch } from './LayoutAdsSettings';
import { useCustomToast } from '../../../../Contexts/ToastContext';
import InteractiveLayoutPreview from './InteractiveLayoutPreview';
import { formatDuration } from "../../../../../config/global";

import { useStretchScreensMedia, useUnStretchScreensMedia, useUpdateLayout } from '../../../../../queries/layouts';
import { useRetrieveNewMediaDuration, useUpdateCampaignMutation } from '../../../../../queries';

import { setIsLoading, toggleDialog } from '../../../../../redux/Slices/Global/globalSlice';
import { setScreenDetails } from '../../../../../redux/Slices/layoutSlice';
import { useLayoutScreenContentControls } from './hooks/useLayoutScreenContentControls';

const LayoutInfo = ({ refetch, onPublish }) => {
    const { t } = useTranslation();

    const { showToast } = useCustomToast();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const location = useLocation();
    const { id } = useParams();

    const { layoutAspectRatio, selectedLayout, screenDetails, screenMedia, screens_setting, isAdsOn } = useSelector((state) => state.layout);
    const { control, handleSubmit } = useForm({ defaultValues: { layoutName: selectedLayout.title } });
    const isAnimatedLayout = location.pathname.startsWith('/apps/animated_layouts');
    const animated = window.location.pathname.includes("animated")
        ? "animated"
        : "normal";
    const { mutate: updateCampaign, isLoading: updating } = useUpdateCampaignMutation();
    const { mutate: stretchScreenMedia, isLoading: isStretching } = useStretchScreensMedia();
    const { mutate: unstretchScreenMedia, isLoading: isUnStretching } = useUnStretchScreensMedia();
    const { mutate: updateLayout, isLoading } = useUpdateLayout();

    const [isEditing, setIsEditing] = useState(false);

    const onTitleSubmit = (data) => {
        updateLayout(
            {
                layoutId: id,
                data: {
                    "title": data.layoutName,
                    "status": "ACTIVE",
                },
            },
            {
                onSuccess: () => {
                    setIsEditing(false)
                    showToast('success', t('layouts.setup_update_layout'), t('layouts.setup_title_updated_success'));
                },
            }
        );
    };





    const editAnimatedLayout = () => {
        dispatch(setScreenDetails(screens_setting[0]))
        dispatch(toggleDialog("CreateAnimationLayout"))
    }

    // Callback that matches the signature expected by our hook, but executes the existing updateCampaign logic
    const handleScreenSettingUpdate = (key, value) => {
        // Map the hook's key/value to what updateCampaign expects
        // key is 'is_muted' or 'maintain_aspect_ratio'
        // value is 1/0
        
        // dispatch(setIsLoading(true)); // Optional: might be too jarring if optimistic? 
        // We probably want to keep the spinner for the Area Setting update itself since it's "Saving".
        // BUT the hook is debounced. If we trigger this immediately, we get 2 behaviors.
        // The user wants "same thing".
        // In AreaControls, the area setting update was local, then saved later?
        // HERE, it's instant save.
        // Let's fire it immediately for the *screen setting* (persisting default), 
        // while the bulk update happens in background.
        
        // Construct the update payload based on which key changed
        const newSettings = {
            is_muted: key === 'is_muted' ? value : screenDetails?.settings?.live?.is_muted,
            maintain_aspect_ratio: key === 'maintain_aspect_ratio' ? value : screenDetails?.settings?.live?.maintain_aspect_ratio,
        };

        updateCampaign({
            id: screenDetails?.id,
            data: {
                "status": "DRAFT",
                "name": screenDetails?.name,
                "settings": {
                    live: {
                         ...screenDetails?.settings?.live,
                         ...newSettings
                    }
                },
            },
        }, {
            onSuccess: () => {
                // queryClient.invalidateQueries(["layoutData"]);
                // queryClient.invalidateQueries({ queryKey: [`allLayout-${animated}`] });
                // refetch();
                if (onPublish) {
                     // onPublish(); // Removed per user request
                }
            }
        });
    };

    const { mutedChecked, filledChecked, handleUpdate } = useLayoutScreenContentControls(screenDetails, handleScreenSettingUpdate);

    return (
        <div className="card-body h-[85vh] scroll_div overflow-auto">
            <div className="col-md-12 col-lg-12 order-md-1 order-lg-1 ">
                <div className="user-avatar-section">
                    <div className="d-flex justify-center align-items-center flex-column">
                        <div className="text-center w-[100%]   ">
                            <h4 className="mb-1 ">{t('layouts.setup_layout_info')} </h4>
                            <InteractiveLayoutPreview />
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="w-100 text-center">
                        <p className="  mb-2">{layoutAspectRatio?.width}X{layoutAspectRatio?.height} </p>

                        {isEditing ? (
                            <>
                                <form className="flex mt-1 w-100" onSubmit={handleSubmit(onTitleSubmit)}  >
                                    <div className="flex flex-col w-full">
                                        <Controller name="layoutName" control={control} rules={{
                                            required: t('layouts.setup_title_required'),
                                            minLength: { value: 3, message: t('layouts.setup_title_min_length') },
                                        }}
                                            render={({ field, fieldState }) => (
                                                <>
                                                    <InputText
                                                        {...field}
                                                        placeholder={t('layouts.setup_layout_name_placeholder')}
                                                        className={`p-2 border rounded-lg  ${fieldState.error ? "border-red-500" : "border-gray-300"
                                                            }`}
                                                        onChange={(e) => {
                                                            field.onChange(e);
                                                        }}
                                                    />
                                                    {fieldState.error && (
                                                        <span className="text-red-500 text-start text-sm pt-1">{fieldState.error.message}</span>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </div>
                                    <div><Button label="" icon="fa-solid fa-check" className="p-0 me-2 bg-transparent text-black border-transparent " type="submit" /></div>
                                </form>
                            </>
                        ) : (
                            <>
                                <div className="flex justify-center">
                                    <h5 className="mb-0">
                                        {
                                            selectedLayout.title.length > 15
                                                ? selectedLayout.title.substring(0, 15) + "..."
                                                : selectedLayout.title}
                                    </h5>
                                    <Button
                                        label=""
                                        icon="fa-solid fa-pen"
                                        className="p-0 me-2 bg-white text-black border-white"
                                        onClick={() => setIsEditing(true)}
                                    />
                                </div>
                            </>)}
                    </div>
                </div>
            </div>

            <div className="col-md-12 col-lg-12 order-md-1 order-lg-1 ">
                <div className="">
                    <div className="flex">
                        <div className=" w-50 ">
                            <div className="d-flex align-items-start me-4 mt-3 gap-3">
                                <span className="badge bg-label-primary p-2 rounded"><i className="bx bx-windows bx-sm"></i></span>
                                <div>
                                    <span className='text-xs'>{t('layouts.setup_no_of_screens')}</span>
                                    <h5 className="mb-0">{selectedLayout.screens_setting?.length ?? "--"}</h5>
                                </div>
                            </div>
                        </div>
                        <div className=" w-50  ">
                            <div className="d-flex align-items-start me-4 mt-3 gap-3">
                                <span className="badge bg-label-primary p-2 rounded"><i className="bx bx-timer  bx-sm"></i></span>
                                <div>
                                    <span className='text-xs'>{t('layouts.setup_layout_duration_label')}</span>
                                    <h5 className="mb-0">{formatDuration(selectedLayout.layout_duration ?? "0")}</h5>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='mt-3'>
                        {!isAnimatedLayout && (<LayoutAdsSwitch />)}
                        {isAdsOn ?
                            <AdsDurations />
                            : ""}

                    </div>

                    <Button label={t('layouts.setup_edit_layout_btn')} className="mt-4 mx-2 py-2 w-[99%] p-button-outlined" onClick={() => isAnimatedLayout ? editAnimatedLayout() : dispatch(toggleDialog("EditLayoutPopUp"))} />



                </div>
                <hr></hr>
                <div className="row">
                    <div className="user-avatar-section">
                        <div className="d-flex align-items-center flex-column">
                            <div className="  text-center w-[65%] pl-5 ml-5 pe-5">
                                <h5 className="mb-2">{t('layouts.setup_screen_details')} </h5>

                            </div>
                        </div>
                    </div>
                    <div className="flex justify-between  mt-3">
                        <div className="w-100 d-flex align-items-start me-4 gap-3">
                            <span className="fs-5   bg-label-primary px-[12px] py-2 rounded "><i class="fa-regular fa-window-maximize"></i></span>
                            <div>
                                <h4 className="my-2">{screenDetails?.name?.length > 21
                                    ? screenDetails?.name?.substring(0, 21) + "..."
                                    : screenDetails?.name} </h4>
                            </div>
                        </div>

                    </div>
                    <div className='flex justify-between'>
                        <div className="w-50 ">
                            <div className="d-flex align-items-start me-4 mt-3 gap-3">
                                <span className="badge bg-label-primary p-2 rounded"><i className="bx bx-check bx-sm"></i></span>
                                <div>
                                    <span className='text-xs'>{t('layouts.setup_no_of_media')}</span>
                                    <h5 className="mb-0">{screenMedia ?? "--"}</h5>
                                </div>
                            </div>
                        </div>
                        <div className=" w-50">
                            <div className="d-flex align-items-start   mt-3 gap-3">
                                <span className="badge bg-label-primary p-2 rounded"><i className="bx bx-time bx-sm"></i></span>
                                <div>
                                    <span className='text-xs'>{t('layouts.setup_total_duration')}</span>
                                    <h5 className="mb-0">{formatDuration(screenDetails?.campaign_duration ?? "0")}</h5>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className=" mt-3 row justify-center">
                    <hr ></hr>
                    <div className="user-avatar-section">
                        <div className="d-flex align-items-center flex-column">
                            <h5 className="mb-2">{t('table.actions')} </h5>
                            <div className="d-flex justify-end w-100">
                                {updating ? <ProgressSpinner className='w-[25px] h-[25px] mx-0' strokeWidth="3" /> : <></>}
                            </div>
                        </div>
                        <div className="overflow-y-auto overflow-x-hidden  ">
                            {/* ads and animation */}
                            {isAnimatedLayout ? "" : isAdsOn && (
                                <>
                                    <ScreenAdsSwitch />

                                </>
                            )}

                            {/* mute section  */}
                            <div className="w-100 d-flex justify-content-between align-items-center me-4 mt-3 gap-3">
                                <div className='d-flex align-items-center   gap-3'>
                                    <span className="fs-4 bg-label-primary px-2 py-1 w-[40.8px] h-[40.8px] rounded text-center">
                                        {!mutedChecked ? <i className="fa-solid fa-volume-high fa-sm"></i> : <i className="fa-solid fa-volume-xmark fa-sm"></i>}
                                    </span>
                                    <div>
                                        <span className="text-xs">{t('layouts.setup_screen_audio')}: {mutedChecked ? t('layouts.setup_muted') : t('layouts.setup_not_muted')}</span>
                                        <h5 className=" ">{t('layouts.setup_mute_all_media')}</h5>
                                    </div>
                                </div>
                                <div className='d-flex align-items-center   gap-3'>
                                    <InputSwitch disabled={updating} checked={mutedChecked}
                                        onChange={(e) => handleUpdate('is_muted', e.value ? 1 : 0)} />
                                </div>
                            </div>
                            {/* stretch section  */}
                            <div className="w-100 d-flex justify-content-between align-items-center me-4 mt-3 gap-3">
                                <div className='d-flex align-items-center   gap-3'>
                                    <span className="fs-4 bg-label-primary w-[40.8px] h-[40.8px] px-2 py-1 rounded text-center">
                                        {filledChecked ? <i class="fa-solid fa-panorama fa-sm"></i> : <i class="fa-solid fa-image fa-sm"></i>}
                                    </span>
                                    <div className='flex items-center'>
                                        {/* <span className="text-xs">Screen's Media: {screenDetails?.settings?.live?.maintain_aspect_ratio ? "Fit Screen" : "Disable fit to screen"}</span> */}
                                        <h5 className=" mb-0">{t('layouts.setup_fill_to_screen')}</h5>
                                    </div>
                                </div>

                                <div className='d-flex align-items-center   gap-3' id="stretchSwitchWrapper" style={{ display: "inline-block", position: "relative" }}>
                                    {isStretching || isUnStretching ? <ProgressSpinner className='w-[25px] h-[25px]' strokeWidth="3" /> : <> </>}
                                    <InputSwitch disabled={isStretching || isUnStretching} checked={filledChecked}
                                        onChange={(e) => handleUpdate('maintain_aspect_ratio', e.value ? 0 : 1)} />

                                </div>
                            </div>



                        </div>
                    </div>
                </div >

            </div >
        </div >

    );
};

export default LayoutInfo;